<template>
  <GmvMarker
    v-if="!isPublicInactive && shouldShowMarker"
    ref="markerRef"
    :position="coords"
    :content="markerContent"
    :gmpClickable="clickable && !widget"
    :gmpDraggable="moving"
    :zIndex="zIndex"
    :markerKey="`marker-${deviceId}`"
    data-cy="marker"
    @gmp-click="clicked"
    :title="title"
    @dragend="onDragend($event)"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { DeviceType } from '@/api/services/utils'
import { buildAdvancedMarkerContent } from '@/services/map'
import { getPermissionsByDeviceId } from '@/services/subscriptions'
import { measurementTypesKey } from '@/services/device-measurements'
import type { FormattedMeasurement } from '@/services/device-service/devices'
import { SlugsEnum } from '@/permissions/SlugsEnum'
import { getUserAccountId } from '@/helpers/loginas/logInAsHelper'
import { NAMESPACES } from '@/store/types'
import { DEVICE_GETTERS, DEVICE_ACTIONS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { ALERTS_GETTERS } from '@/store/alerts'
import { MAP_GETTERS } from '@/store/map'
import { getEffectiveMeasurement } from '@/store/map/utils'
import { getMeasurementValueByName } from '@/components/devicePopover/utils'

// Minimal interface for what GmvMarker exposes via its markerPromise.
interface GmvMarkerRef {
  markerPromise: Promise<{ content: HTMLElement | null } | undefined>
}

interface GoogleMapsMouseEvent {
  latLng: LatLng | null
}

interface LatLng {
  lat(): number
  lng(): number
}

export default defineComponent({
  name: 'MapPin',
  emits: ['clicked', 'closed'],
  props: {
    deviceId: String,
    moving: Boolean,
    widget: Boolean,
    clickable: Boolean,
    zIndex: Number,
    opacity: Number
  },
  data() {
    return {
      accountId: null as string | null
    }
  },
  async mounted() {
    this.accountId = await getUserAccountId(this.$auth)
  },
  computed: {
    id(): string {
      return this.deviceId ? this.deviceId : ''
    },
    deviceType(): DeviceType {
      if (this.isMine) return this.$api.deviceType.USER
      if (this.isShared) return this.$api.deviceType.SHARED

      return this.$api.deviceType.PUBLIC
    },
    deviceModel(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](
        this.id
      )
    },
    isMine(): boolean {
      return (
        this.accountId ===
        this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID}`](
          this.id
        )
      )
    },
    isShared(): boolean {
      if (this.isMine) return false
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`
      ](this.id)
    },
    isIndoor(): boolean {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_INDOOR_BY_DEVICE_ID}`
      ](this.id)
    },
    isPublic(): boolean {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_PUBLIC_BY_DEVICE_ID}`
      ](this.id)
    },
    isDeviceConnected(): boolean {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_CONNECTED_BY_DEVICE_ID}`
      ](this.id)
    },
    badge(): number {
      return this.$store.getters[
        `${NAMESPACES.ALERTS}/${ALERTS_GETTERS.GET_NOTIFICATIONS_NUM_BY_DEVICE_ID}`
      ](this.id)
    },
    isPublicInactive(): boolean {
      return !this.isMine && !this.isShared && !this.isDeviceConnected
    },
    aqiType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_AQI_TYPE}`]
    },
    measurementType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`]
    },
    currentReading(): number | null {
      if (this.isDeviceConnected) {
        const measurementName = this.getType(this.measurementType, this.aqiType)
        const sensorReadings: Record<string, FormattedMeasurement> = this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SENSOR_READINGS_BY_DEVICE_ID}`
        ](this.id)
        return getMeasurementValueByName(sensorReadings, measurementName, undefined)
      }
      return null
    },
    lastTelemetryTimestamp(): string | null {
      return this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LAST_TELEMETRY_TIMESTAMP_BY_DEVICE_ID}`
      ](this.id)
    },
    markerContent(): HTMLDivElement {
      const productFamily = this.$store.getters[
        `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_PRODUCT_FAMILY}`
      ](this.deviceModel)
      return buildAdvancedMarkerContent(
        productFamily,
        this.currentReading,
        this.isIndoor,
        this.moving,
        this.badge,
        this.aqiType,
        this.measurementType
      )
    },
    coords(): { lat: number; lng: number } {
      const nCoords = this.$store.getters[
        `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_COORDS_BY_DEVICE_ID}`
      ](this.id)
      return nCoords && nCoords.lat != null ? nCoords : { lat: NaN, lng: NaN }
    },

    visibleDeviceModels(): string[] {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_DEVICE_MODELS}`]
    },
    shouldShowMarker(): boolean {
      let visibility = true
      if (this.moving) return true
      if (this.isIndoor && !this.$store.state.map.shownSettings.showIndoor) {
        visibility = false
      }

      if (!this.isIndoor && !this.$store.state.map.shownSettings.showOutdoor) {
        visibility = false
      }

      if (this.isMine && !this.$store.state.map.shownSettings.showMine) {
        visibility = false
      }

      if (
        !this.isMine &&
        !this.isShared &&
        this.isPublic &&
        !this.$store.state.map.shownSettings.showPublic
      ) {
        visibility = false
      }

      if (!this.isMine && this.isShared && !this.$store.state.map.shownSettings.showShared) {
        visibility = false
      }

      if (!this.visibleDeviceModels || !this.visibleDeviceModels.includes(this.deviceModel)) {
        visibility = false
      }

      if (!this.hasPermissions) {
        visibility = false
      }

      if (!visibility) this.$emit('closed', this.id)

      return visibility
    },
    serial(): string {
      return this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID}`](
        this.id
      )
    },
    hasPermissions(): boolean {
      //only check if it is ours
      if (!this.isMine) {
        return true
      }

      // Permission allowance is now checked via matching slug associated with roles on a per device basis
      const deviceSlugPermissions = getPermissionsByDeviceId(this.deviceId)
      const deviceHasSlugPermissions = deviceSlugPermissions.includes(SlugsEnum.MapViewPin)
      return deviceHasSlugPermissions
    },
    title(): string {
      // Serial numbers are present only for owned and shared devices
      return this.serial ? `Map pin for device ${this.serial}` : ''
    }
  },
  watch: {
    // GmvMarker uses fast-deep-equal to guard its internal prop→setter binding. Because
    // HTMLDivElement properties are non-enumerable prototype setters, Object.keys(el) returns []
    // for any DOM element — so the library always treats two different elements as equal and
    // never calls marker.content = newValue after the initial render. We work around this by
    // awaiting the AdvancedMarkerElement instance (exposed as markerPromise) and setting
    // marker.content directly whenever markerContent recomputes.
    async markerContent(newContent: HTMLDivElement) {
      const markerRef = this.$refs.markerRef as GmvMarkerRef | null
      if (!markerRef?.markerPromise) return
      const marker = await markerRef.markerPromise
      if (marker) {
        marker.content = newContent
      }
    }
  },
  methods: {
    clicked(): void {
      this.$emit('clicked', {
        deviceId: this.deviceId,
        deviceType: this.deviceType,
        coords: this.coords
      })
    },
    onDragend(event: GoogleMapsMouseEvent): void {
      if (!event.latLng) {
        return
      }

      const lat = parseFloat(event.latLng.lat().toFixed(8))
      const lng = parseFloat(event.latLng.lng().toFixed(8))

      this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.UPDATE_DEVICE_COORDS}`, {
        deviceId: this.id,
        coords: { lat, lng }
      })
    },
    getType(measurementType: number, aqiType: number): string {
      const effectiveType = getEffectiveMeasurement(measurementType, aqiType)
      return measurementTypesKey[effectiveType as keyof typeof measurementTypesKey]
    }
  }
})
</script>

<style lang="scss">
.map-advanced-marker {
  position: relative;
  display: inline-block;
  line-height: 0;
}

.map-advanced-marker-label {
  position: absolute;
  left: var(--map-marker-label-left);
  top: var(--map-marker-label-top);
  transform: translate(-50%, -50%);
  pointer-events: none;
  color: var(--map-marker-label-color);
  font-size: var(--map-marker-label-font-size);
  font-weight: var(--map-marker-label-font-weight);
  font-family: 'Roboto', sans-serif;
  line-height: 1;
}
</style>
